// @flow

import React, { Component } from 'react';
import i18n from 'app/common/lib/i18n';
import PropTypes from 'prop-types';
import type { CollaboratorType } from 'app/common/react/components/BoardCreate/BoardCreate';
import Typeahead from 'app/common/react/ui/deprecated/Typeahead'; // eslint-disable-line pinterest/no-deprecated-imports
import withResourceLegacy from 'app/common/lib/react/withResourceLegacy'; // eslint-disable-line pinterest/no-deprecated-imports
import { isEmailAddress } from 'app/common/lib/StringUtil';
import { makeContact } from 'app/common/react/components/BoardCreate/Contact';
import { Box, Card, Icon, Text, SearchField, Touchable } from 'gestalt';
import { inviteEmails, inviteUsers } from 'app/common/redux/actions/board/collab';

export type InviteType = {
  id: string,
  resource: (string, Array<string>) => void,
};

type Props = {|
  onSelectedCollaboratorsChange: (InviteType) => void,
  resourceLoaded: boolean,
  shareSuggestions: Array<CollaboratorType>,
  inCollabInBoardCreateExperiment: boolean,
|};

function isUniqueContact(contact, idx, array) {
  const { id } = contact;
  // Find the first index of contact in the array.
  // If it occurs before `idx`, we know contact isn't unique.
  return idx === array.findIndex((contact) => contact.id === id);
}

type State = {
  selectedCollaborators: Array<CollaboratorType>,
  typeaheadSuggestions: Array<CollaboratorType>,
  showCollabList: boolean,
  value: string,
};

class BoardCreateCollaborators extends Component<Props, State> {
  static propTypes = {
    onSelectedCollaboratorsChange: PropTypes.func.isRequired,
    resourceLoaded: PropTypes.bool.isRequired,
    shareSuggestions: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
      }),
    ).isRequired,
  };
  static defaultProps = {
    shareSuggestions: [],
  };

  state = {
    selectedCollaborators: [],
    showCollabList: false,
    showSocialConnectPanel: false,
    typeaheadSuggestions: [],
    value: '',
  };

  handleTextFieldChange = ({ value }) => {
    this.setState({ value });
  };

  handleCollaboratorClick = (collaborator: CollaboratorType) => {
    const { id, type } = collaborator;
    this.isIdSelected[id] = !this.isIdSelected[id];

    const { selectedCollaborators } = this.state;
    const newSelectedCollaborators = selectedCollaborators.concat([collaborator]);
    const resource = type === 'email' ? inviteEmails : inviteUsers;

    this.props.onSelectedCollaboratorsChange({ id, resource });
    this.setState({
      selectedCollaborators: newSelectedCollaborators,
    });
  };

  handleTypeaheadSuggestionsChange = (typeaheadSuggestions: Array<CollaboratorType>) => {
    this.setState({ typeaheadSuggestions });
  };

  handleToggleCollabList = () => {
    this.setState((prevState) => ({ showCollabList: !prevState.showCollabList }));
  };

  isIdSelected: { [id: string]: boolean } = {};

  render() {
    const { value, showCollabList } = this.state;
    const { inCollabInBoardCreateExperiment } = this.props;

    const results =
      value === ''
        ? this.props.shareSuggestions
        : this.state.selectedCollaborators
            .concat(this.state.typeaheadSuggestions)
            .concat({
              type: 'email',
              id: value,
              valid: isEmailAddress(value),
            })
            .concat(this.props.shareSuggestions)
            .filter(isUniqueContact);

    const renderedResults = results
      .map((collaborator, idx) =>
        makeContact(collaborator, this.isIdSelected[collaborator.id], this.handleCollaboratorClick),
      )
      .map((el, idx) => <li key={idx}>{el}</li>);

    const header = inCollabInBoardCreateExperiment ? (
      <Touchable onTouch={this.handleToggleCollabList}>
        <Card>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="between"
            paddingX={3}
            marginTop={3}
            marignBottom={1}
            paddingY={3}
          >
            <Text size="md">
              {i18n._(
                'Add collaborators (optional)',
                'Optional field to add collaborators to a board on board create',
              )}
            </Text>
            <Icon accessibilityLabel="" icon={showCollabList ? 'arrow-down' : 'arrow-forward'} />
          </Box>
        </Card>
      </Touchable>
    ) : (
      <Box paddingX={3} marginBottom={4} marginTop={3} paddingY={3}>
        <Text size="md">
          {i18n._(
            'Add collaborators (optional)',
            'Optional field to add collaborators to a board on board create',
          )}
        </Text>
      </Box>
    );
    return (
      <Box display="flex" direction="column" height="100%">
        {header}
        {((inCollabInBoardCreateExperiment && showCollabList) ||
          !inCollabInBoardCreateExperiment) && (
          <React.Fragment>
            <Box paddingX={3} marginTop={2} marginBottom={4} width="100%">
              <Box flex="grow">
                <Typeahead
                  onTypeaheadSuggestionsChange={this.handleTypeaheadSuggestionsChange}
                  resourceName="ShareSuggestionsTypeaheadResource"
                  value={value}
                >
                  <SearchField
                    id="CollaboratorInviteSearchField"
                    accessibilityLabel={i18n._('Search for collaborators by name or email')}
                    onChange={this.handleTextFieldChange}
                    placeholder={i18n._('Search by name or email')}
                    value={value}
                  />
                </Typeahead>
              </Box>
            </Box>
            <Box paddingX={3} overflow="scrollY">
              <ul>{renderedResults}</ul>
            </Box>
          </React.Fragment>
        )}
      </Box>
    );
  }
}

export default withResourceLegacy(
  {
    name: 'ShareSuggestionsResource',
    key: 'shareSuggestions',
  },
  { allowStale: false },
)(BoardCreateCollaborators);
